import React, { useEffect, useRef, useState } from 'react';

import { TopWinsDateRange, TopWinsOrderType } from "../../../../../../../enums";
import { useTranslation } from '../../../../../../../hooks';
import { T } from '../../../../../../../constants';
import { Filter } from "../../../filter";

import './style.scss';

export enum TopWinsTabKey {
  MyTopWins = 'myTopWins',
  AllTopWins = 'allTopWins',
}

interface TopWinsTab {
  key: TopWinsTabKey;
  label: string;
}

interface TopWinsTabsProps {
  tabs?: TopWinsTab[];
  orderType: TopWinsOrderType;
  activeTab?: TopWinsTabKey;
  dateRange: TopWinsDateRange;
  onChange?: (tab: TopWinsTabKey) => void;
  onFilterApply: (orderType: TopWinsOrderType, dateRange: TopWinsDateRange) => void;
}

const DEFAULT_TABS: TopWinsTab[] = [
  { key: TopWinsTabKey.MyTopWins, label: T.MY_TOP_WINS },
  { key: TopWinsTabKey.AllTopWins, label: T.ALL_TOP_WINS },
];

export const TopWinsTabs: React.FC<TopWinsTabsProps> = ({
  tabs = DEFAULT_TABS,
  activeTab,
  onChange,
  orderType,
  dateRange,
  onFilterApply,
}) => {

  const t = useTranslation();

  const [internalActive, setInternalActive] = useState<TopWinsTabKey>(
    activeTab ?? tabs[0]?.key
  );
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const filterWrpRef = useRef<HTMLDivElement>(null);

  const current = activeTab ?? internalActive;

  const activeIndex = Math.max(0, tabs.findIndex(({ key }) => key === current));

  const handleClick = (key: TopWinsTabKey) => {
    if (activeTab === undefined) setInternalActive(key);
    onChange?.(key);
  };

  const togglePopup = () => setIsPopupOpen(prev => !prev);
  const closePopup = () => setIsPopupOpen(false);

  // The ref wraps the toggle button too — a mousedown on it must not close the
  // popup here, or the button's click would immediately reopen it.
  useEffect(() => {
    if (!isPopupOpen) return;

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (filterWrpRef.current && !filterWrpRef.current.contains(event.target as Node)) {
        setIsPopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isPopupOpen]);

  const handleFilterApply = (nextOrderType: TopWinsOrderType, nextDateRange: TopWinsDateRange) => {
    onFilterApply(nextOrderType, nextDateRange);
    closePopup();
  };

  return (
    <div className="topWinsTabsBar">
      <div
        className="topWinsTabs"
        style={{
          ['--tw-count' as string]: tabs.length,
          ['--tw-translate' as string]: `${activeIndex * 100}%`,
        } as React.CSSProperties}
      >
        <span className="topWinsTabIndicator" />
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`topWinsTab ${current === key ? 'topWinsTabActive' : ''}`}
            onClick={() => handleClick(key)}
          >
            <span className="topWinsTabLabel">{t(label)}</span>
          </button>

        ))}
      </div>

        <div className="topWinsTabFilterBtnWrp" ref={filterWrpRef}>
            <button
                type="button"
                className={`topWinsTabFilterBtn ${isPopupOpen ? 'topWinsTabFilterBtnActive' : ''}`}
                onClick={togglePopup}
                aria-label="filters"
            >
                <i className="icon-filter-tabs topWinsTabFilterIcon" />
            </button>

            {isPopupOpen && (
                <div className="topWinsTabPopupOverlay" onClick={closePopup}>
                    <div
                        className="topWinsTabPopup"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Filter
                            orderType={orderType}
                            dateRange={dateRange}
                            onApply={handleFilterApply}
                        />
                    </div>
                </div>
            )}
        </div>
    </div>
  );
};
